import React, { useState, useEffect } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { FaKey, FaCopy, FaSignInAlt, FaRocket, FaSyncAlt, FaCheckCircle, FaCookieBite } from 'react-icons/fa';
import { TechSection, TechButton, TechButtonGhost, TechInput, StatusPill } from '../../styles/techComponents';
import { getDummyCredentials, testLoginProfile, activateDummyAccounts, autoExtractCookies } from '../../services/digitalProfilesService';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const Card = styled.div`
  background: ${p => p.$active ? 'rgba(0, 180, 216, 0.08)' : 'rgba(30, 41, 59, 0.6)'};
  border: 1px solid ${p => p.$active ? 'var(--accent)' : 'var(--border-soft)'};
  border-radius: 8px;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
`;

const AccountHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-soft);
  padding-bottom: 0.5rem;
  
  .user {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.95rem;
  }
  
  .platform {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: var(--text-disabled);
    font-family: var(--font-mono);
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  
  label {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: var(--text-disabled);
    letter-spacing: 0.08em;
  }
  
  .value-row {
    display: flex;
    gap: 0.3rem;
    align-items: center;
  }
  
  input {
    flex: 1;
    font-size: 0.8rem;
  }
`;

const CopyBtn = styled.button`
  background: transparent;
  border: 1px solid var(--border-soft);
  color: var(--text-secondary);
  padding: 0.25rem 0.4rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.15s;
  
  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.25rem;
  flex-wrap: wrap;
`;

const Warning = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  color: #ffc107;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  margin-bottom: 0.75rem;
`;

const Status = styled.span`
  font-size: 0.75rem;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  background: ${p => p.$success ? 'rgba(56, 211, 159, 0.15)' : p.$error ? 'rgba(255, 84, 112, 0.15)' : 'rgba(255, 193, 7, 0.15)'};
  color: ${p => p.$success ? 'var(--alert-success)' : p.$error ? 'var(--alert-error)' : '#ffc107'};
`;

const PanelCredenciales = ({ profiles, onRefresh }) => {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [message, setMessage] = useState(null);
  const [loginStatus, setLoginStatus] = useState({});
  const [extractStatus, setExtractStatus] = useState({});

  const loadCreds = async () => {
    setLoading(true);
    try {
      const data = await getDummyCredentials();
      setCreds(data.credentials || []);
    } catch (err) {
      setMessage({ type: 'error', text: friendlyError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreds();
  }, [profiles]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: `${label} copiado` });
    setTimeout(() => setMessage(null), 2000);
  };

  const handleLogin = async (id) => {
    setLoginStatus(prev => ({ ...prev, [id]: 'loading' }));
    try {
      const result = await testLoginProfile(id);
      setLoginStatus(prev => ({ ...prev, [id]: result.success ? 'success' : 'error' }));
      setMessage({ type: result.success ? 'success' : 'error', text: `@${result.account?.username}: ${result.message}` });
    } catch (err) {
      setLoginStatus(prev => ({ ...prev, [id]: 'error' }));
      setMessage({ type: 'error', text: friendlyError(err) });
    }
  };

  const handleAutoExtract = async (id) => {
    setExtractStatus(prev => ({ ...prev, [id]: 'loading' }));
    try {
      const result = await autoExtractCookies(id);
      setExtractStatus(prev => ({ ...prev, [id]: result.ok ? 'success' : 'error' }));
      setMessage({
        type: result.ok ? 'success' : 'error',
        text: result.ok
          ? `@${result.account?.username}: cookies extraídas automáticamente`
          : `Auto-extract falló: ${result.error || result.message || 'error desconocido'}`
      });
      if (result.ok) onRefresh?.();
    } catch (err) {
      setExtractStatus(prev => ({ ...prev, [id]: 'error' }));
      setMessage({ type: 'error', text: friendlyError(err) });
    }
  };

  const activateAll = async () => {
    if (creds.length === 0) return;
    setActivating(true);
    try {
      const ids = creds.map(c => c.id);
      const result = await activateDummyAccounts({ action: 'start', accountIds: ids, interval: 10000 });
      setMessage({ type: 'success', text: `Activadas ${result.active_count} cuentas` });
      onRefresh?.();
    } catch (err) {
      setMessage({ type: 'error', text: friendlyError(err) });
    } finally {
      setActivating(false);
    }
  };

  const loginAll = async () => {
    for (const c of creds) {
      await handleLogin(c.id);
      await new Promise(r => setTimeout(r, 500));
    }
  };

  return (
    <TechSection
      title="🔑 Credenciales y Login Rápido"
      action={
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusPill>{creds.length} cuentas</StatusPill>
          <TechButtonGhost onClick={loadCreds} disabled={loading}>
            <FaSyncAlt /> {loading ? 'Cargando...' : 'Recargar'}
          </TechButtonGhost>
          <TechButtonGhost onClick={loginAll}>
            <FaSignInAlt /> Login todos
          </TechButtonGhost>
          <TechButton onClick={activateAll} disabled={activating}>
            <FaRocket /> {activating ? 'Activando...' : 'Activar todos'}
          </TechButton>
        </div>
      }
      style={{ marginBottom: '1.25rem' }}
    >
      <Warning>
        ⚠️ <strong>Modo testing:</strong> Las contraseñas se muestran en texto plano para facilitar pruebas locales. No uses esto en producción real.
      </Warning>

      {message && (
        <div style={{
          padding: '0.5rem 0.75rem',
          borderRadius: '6px',
          marginBottom: '0.75rem',
          background: message.type === 'success' ? 'rgba(56, 211, 159, 0.15)' : 'rgba(255, 84, 112, 0.15)',
          color: message.type === 'success' ? 'var(--alert-success)' : 'var(--alert-error)',
          fontSize: '0.85rem'
        }}>
          {message.text}
        </div>
      )}

      <Grid>
        {creds.map(c => (
          <Card key={c.id} $active={c.status === 'active'}>
            <AccountHeader>
              <div>
                <div className="user">@{c.username}</div>
                <div className="platform">{c.platform} · {c.status || 'inactive'}</div>
              </div>
              <Status $success={loginStatus[c.id] === 'success'} $error={loginStatus[c.id] === 'error'}>
                {loginStatus[c.id] === 'loading' ? <FaSyncAlt /> : loginStatus[c.id] === 'success' ? <FaCheckCircle /> : loginStatus[c.id] === 'error' ? 'Error' : 'Pendiente'}
              </Status>
            </AccountHeader>

            <Field>
              <label>Usuario / Login</label>
              <div className="value-row">
                <TechInput value={c.login_user} readOnly />
                <CopyBtn onClick={() => copy(c.login_user, 'Usuario')}><FaCopy /></CopyBtn>
              </div>
            </Field>

            <Field>
              <label>Contraseña</label>
              <div className="value-row">
                <TechInput type="text" value={c.password} readOnly />
                <CopyBtn onClick={() => copy(c.password, 'Contraseña')}><FaCopy /></CopyBtn>
              </div>
            </Field>

            <Field>
              <label>Email</label>
              <div className="value-row">
                <TechInput value={c.email_address} readOnly />
                <CopyBtn onClick={() => copy(c.email_address, 'Email')}><FaCopy /></CopyBtn>
              </div>
            </Field>

            <ActionRow>
              <TechButtonGhost onClick={() => handleLogin(c.id)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}>
                <FaSignInAlt /> Test Login
              </TechButtonGhost>
              <TechButtonGhost
                onClick={() => handleAutoExtract(c.id)}
                disabled={extractStatus[c.id] === 'loading'}
                style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem' }}
              >
                {extractStatus[c.id] === 'loading' ? <FaSyncAlt /> : <FaCookieBite />}
                {extractStatus[c.id] === 'loading' ? ' Extrayendo...' : ' Auto cookies'}
              </TechButtonGhost>
              <CopyBtn onClick={() => copy(`${c.login_user}\t${c.password}\t${c.email_address}`, 'Todo')}>
                <FaKey /> Copiar todo
              </CopyBtn>
            </ActionRow>
          </Card>
        ))}
      </Grid>
    </TechSection>
  );
};

export default PanelCredenciales;
